import { useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { PasswordField } from '@/components/ui/password-field';
import verified from '@/assets/verified.svg';
import { changePassword } from '@/features/auth/api/auth-api';
import { FORM_ERROR, errorMessage } from '@/features/auth/use-auth-error';
import { isStrongPassword } from '@/lib/validation';

interface ChangePasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} title="Update your password" hideTitle>
      <Body onClose={onClose} />
    </Dialog>
  );
}

function Body({ onClose }: { onClose: () => void }) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<ChangePasswordForm>();
  const mutation = useMutation({
    mutationFn: (v: ChangePasswordForm) => changePassword(v.oldPassword, v.newPassword),
  });
  const newPassword = useWatch({ control, name: 'newPassword' }) ?? '';

  if (mutation.isSuccess) {
    return (
      <div className="stack center">
        <img src={verified} alt="" className="art" style={{ margin: '0 auto' }} />
        <h4>Your password has been updated.</h4>
        <p>Please use your new password to log in the next time you visit.</p>
        <Button block onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;
  return (
    <form
      className="stack"
      noValidate
      onSubmit={(e) =>
        void handleSubmit((v) => {
          mutation.mutate(v);
        })(e)
      }
    >
      <h3>Update your password</h3>
      {mutation.isError && <Alert>{errorMessage(mutation.error)}</Alert>}
      {!mutation.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}
      <PasswordField
        id="changePassword-oldPassword"
        label="Current Password"
        autoComplete="current-password"
        error={errors.oldPassword?.message}
        {...register('oldPassword', { required: 'Please provide a valid password.' })}
      />
      <PasswordField
        id="changePassword-password"
        label="New Password"
        autoComplete="new-password"
        showRequirements
        currentValue={newPassword}
        error={errors.newPassword?.message}
        {...register('newPassword', {
          required: 'Please provide a valid password.',
          validate: (p) => isStrongPassword(p) || 'Please provide a valid password.',
        })}
      />
      <PasswordField
        id="changePassword-confirmPassword"
        label="Confirm New Password"
        autoComplete="new-password"
        error={errors.confirmPassword?.message}
        {...register('confirmPassword', {
          validate: (c) => c === newPassword || 'New Passwords must match.',
        })}
      />
      <div className="dialog__actions">
        <Button type="submit" block busy={mutation.isPending}>
          Update
        </Button>
        <Button variant="ghost" block onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
